"use client";

import PercentageInvoiceFields from '@/components/finance/PercentageInvoiceFields';
import { newPercentageDraft, validPercentage } from '@/utils/percentage-billing';

import InvoicePresentationFields from "@/components/finance/InvoicePresentationFields";
import ContractInvoiceLauncher from '@/components/finance/ContractInvoiceLauncher';
import InitialPaymentFields from '@/components/finance/InitialPaymentFields';
import { validInitialPayment, type InitialPaymentDraft } from '@/utils/invoice-payment';

import ClientInvoiceSettings, { type InvoiceDefaults } from "@/components/finance/ClientInvoiceSettings";
import { type LineDraft, linesFromApi } from "@/components/crm/LineItemsEditor";
import DeliveredItemsEditor, { emptyDeliveredQuantities, deliveredQuantitiesPayload } from "@/components/finance/DeliveredItemsEditor";
import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider, DataTableHeader, DataTableHeaderGroup, DataTableHead, DataTableBody, DataTableRow, DataTableCell,
} from "../../components/ui/data-table";
import { get, post, put, can } from "../../utils/service";
import { formatDate, parseApiError, showMoney, showTotal, originalHint, MISTAKE_REASONS, type Paginated } from "@/utils/finance";
import StatusBadge from "@/components/finance/StatusBadge";
import { GridProvider, GridToolbar, StatsStrip, PaginationBar } from "@/components/crm/DataGrid";
import Modal, { btnPrimary, btnGhost, btnDanger, inputCls } from "@/components/finance/Modal";
import AppSelect from "@/components/ui/app-select";

interface Totals { currency: string; billed: string; paid: string; outstanding: string; overdue: string; invoice_count: number }
interface ContractRow {
  id: string; service_type: string; title?: string | null; start_date: string; end_date: string; value: string | null; currency: string; billing_cycle: string;
  contract_billed_total?: string; billing_mode?: string; deliverables?: Array<Record<string, any>>; invoice_settings?: InvoiceDefaults; status: string; periods_total: number; periods_missing: number; invoices_count: number;
  display_currency?: string; display_value?: string | null;
}
interface Mistake { id: string; invoice_number: string; period_start: string | null; reason: string }
interface Summary { suspected_mistakes: Mistake[]; account: { id: string; name: string; email: string | null }; totals: Totals[]; display_totals: (Omit<Totals, "invoice_count">) | null; contracts: ContractRow[] }
interface Invoice {
  id: string; invoice_number: string; period_start: string | null; period_end: string | null; amount: string; balance: string; amount_paid: string;
  currency: string; effective_status: string; due_date: string; issued_date: string; is_backdated: boolean; contract_id: string | null;
  is_ignored?: boolean;
  contract?: { service_type: string } | null; suspected_mistake?: string | null;
  display_currency?: string; display_amount?: string | null; display_balance?: string | null;
}
interface Period { period_start: string; period_end: string; status: string; invoice: { id: string; invoice_number: string; status: string } | null }

const STATUS_OPTIONS = [["", "All statuses"], ["unpaid", "Unpaid"], ["partially_paid", "Partially paid"], ["paid", "Paid"], ["overdue", "Overdue"], ["cancelled", "Cancelled"], ["ignored", "Ignored invoices"]];

export default function ClientInvoicesPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const admin = can("invoices.delete");
  const [percentageDraft, setPercentageDraft] = useState(newPercentageDraft);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [genLines, setGenLines] = useState<LineDraft[]>([]);
  const [genQuantities, setGenQuantities] = useState<string[]>([]);
  const [genDescription, setGenDescription] = useState("");
  const [genDefaults, setGenDefaults] = useState<InvoiceDefaults>({});
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupError, setCleanupError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const [summary, setSummary] = useState<Summary | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [status, setStatus] = useState("");
  const [contractId, setContractId] = useState("");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [pager, setPager] = useState({ total: 0, total_pages: 0 });

  const [genContract, setGenContract] = useState<ContractRow | null>(null);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initialPayment, setInitialPayment] = useState<InitialPaymentDraft | null>(null);
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState("");
  const [notice, setNotice] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const r = await get<{ data: Summary }>(`/accounts/${clientId}/invoice-summary`);
      setSummary(r.data);
    } catch {
      setNotFound(true);
    }
  }, [clientId]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get<Paginated<Invoice>>("/invoices", {
        account_id: clientId, page, page_size: perPage, status: status || undefined, contract_id: contractId || undefined,
        period_from: periodFrom || undefined, period_to: periodTo || undefined,
      });
      setInvoices(r.data);
      setPager({ total: r.pagination.total, total_pages: r.pagination.total_pages });
    } finally {
      setLoading(false);
    }
  }, [clientId, page, perPage, status, contractId, periodFrom, periodTo]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Deep link from the Accounts page / contract creation: open the generator for that contract.
  useEffect(() => {
    const target = searchParams.get("generate");
    if (!target || !summary) return;
    const c = summary.contracts.find((x) => x.id === target);
    if (c) { openGenerate(c); }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, searchParams]);
  useEffect(() => { setPage(1); }, [status, contractId, periodFrom, periodTo]);
  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  const bulkDelete = async (ids: string[]) => {
    setCleanupBusy(true);
    setCleanupError("");
    try {
      const r = await post<{ data: { deleted: string[]; skipped: { invoice_number: string; reason: string }[] } }>("/invoices/bulk-delete", { ids });
      setCleanupOpen(false);
      setNotice(`Deleted ${r.data.deleted.length} invoice${r.data.deleted.length === 1 ? "" : "s"}${r.data.skipped.length ? `; ${r.data.skipped.length} skipped (${r.data.skipped.map((x) => x.invoice_number).join(", ")} have payments)` : ""}.`);
      await Promise.all([loadSummary(), loadInvoices()]);
    } catch (e) {
      setCleanupError(parseApiError(e).message);
    } finally {
      setCleanupBusy(false);
    }
  };

  const openGenerate = async (c: ContractRow) => {
    setPercentageDraft(newPercentageDraft()); setGenContract(c);
    setGenError("");
    setInitialPayment(null); setPeriods([]); setSelected(new Set()); setGenLines([]); setGenQuantities([]);
    try {
    const clientDefaults = await get<{ data: { settings: InvoiceDefaults; effective_settings?: InvoiceDefaults } }>(`/accounts/${clientId}/invoice-settings`).then((r) => r.data.effective_settings ?? r.data.settings);
    const d = { ...clientDefaults, ...c.invoice_settings };
    setGenDefaults(d); setGenDescription(d.description ?? c.title ?? c.service_type.replace(/_/g, " "));
    if (c.billing_mode === "percentage") return;
    const agreedRates = linesFromApi(c.deliverables?.length ? c.deliverables : d.items);
    setGenLines(agreedRates); setGenQuantities(emptyDeliveredQuantities(agreedRates));
    const r = await get<{ data: { periods: Period[] } }>(`/contracts/${c.id}/billing-periods`);
    setPeriods(r.data.periods);
    setSelected(new Set());
    } catch (e) { setGenError(parseApiError(e).message); }
  };

  const generate = async () => {
    if (!genContract) return;
    setGenBusy(true);
    setGenError("");
    try {
      const r = await post<{ data: { created_count: number } }>(`/contracts/${genContract.id}/generate-invoices`, { ...(genContract.billing_mode === "percentage" ? { billing_percentage: percentageDraft.percentage, billing_label: percentageDraft.label || null, issued_date: percentageDraft.issued_date, generation_key: percentageDraft.generation_key } : { period_starts: Array.from(selected) }), display_name: genDefaults.display_name, delivery_duration: genDefaults.delivery_duration, delivery_start: genDefaults.delivery_start || null, delivery_end: genDefaults.delivery_end || null, ...(initialPayment ? { payment: initialPayment } : {}), ...(genContract.billing_mode === "variable" ? { delivered_items: deliveredQuantitiesPayload(genQuantities), ...(genDescription.trim() ? { description: genDescription } : {}), terms: genDefaults.terms, notes: genDefaults.notes, footer: genDefaults.footer, due_days: genDefaults.due_days } : {}) });
      setNotice(`Generated ${r.data.created_count} invoice${r.data.created_count === 1 ? "" : "s"} for ${genContract.service_type.replace(/_/g, " ")}.`);
      setGenContract(null);
      await Promise.all([loadSummary(), loadInvoices()]);
    } catch (e) {
      setGenError(parseApiError(e).message);
    } finally {
      setGenBusy(false);
    }
  };

  const ignoreInvoice = async (invoiceId: string, ignored: boolean) => {
    try { await put(`/invoices/${invoiceId}`, { is_ignored: ignored }); setNotice(ignored ? 'Invoice ignored. Financial history is retained.' : 'Invoice restored.'); await Promise.all([loadSummary(), loadInvoices()]); }
    catch (failure) { setNotice(parseApiError(failure).message); }
  };

  const columns: ColumnDef<Invoice>[] = [
    { accessorKey: "invoice_number", header: "Invoice", cell: ({ row }) => (<div><p className="text-xs font-semibold text-gray-900">{row.original.invoice_number}</p><p className="text-xs text-gray-500">{row.original.contract?.service_type?.replace(/_/g, " ") ?? "One-off invoice"}</p></div>) },
    { id: "period", header: "Billing period", cell: ({ row }) => (<span className="text-xs text-gray-700">{row.original.period_start ? `${formatDate(row.original.period_start)} – ${formatDate(row.original.period_end)}` : "—"}{row.original.is_backdated && <span className="ml-1 text-[10px] text-gray-400">(backdated)</span>}</span>) },
    { accessorKey: "issued_date", header: "Issued", cell: ({ row }) => <span className="text-xs text-gray-600">{formatDate(row.original.issued_date)}</span> },
    { accessorKey: "due_date", header: "Due", cell: ({ row }) => <span className="text-xs text-gray-600">{formatDate(row.original.due_date)}</span> },
    { accessorKey: "amount", header: "Amount", cell: ({ row }) => <div><span className="text-xs font-semibold">{showMoney(row.original, "amount")}</span>{originalHint(row.original, "amount") && <p className="text-[11px] text-gray-400">billed {originalHint(row.original, "amount")}</p>}</div> },
    { id: "balance", header: "Balance", cell: ({ row }) => <span className="text-xs">{showMoney(row.original, "balance")}</span> },
    { id: "status", header: "Status", cell: ({ row }) => (<div className="flex flex-col items-start gap-1"><StatusBadge kind="invoice" status={row.original.effective_status} />{row.original.is_ignored && <span className="text-xs text-gray-500">Ignored</span>}{can("invoices.update") && <button type="button" className="text-xs text-primary" onClick={(event) => { event.stopPropagation(); ignoreInvoice(row.original.id, !row.original.is_ignored); }}>{row.original.is_ignored ? "Restore invoice" : "Ignore invoice"}</button>}{row.original.suspected_mistake && <span title={MISTAKE_REASONS[row.original.suspected_mistake]} className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">Check · possible mistake</span>}</div>) },
  ];

  if (notFound) return <div className="p-10 text-center text-sm text-gray-500">Account not found. <Link className="text-primary underline" to="/invoices">Back to invoices</Link></div>;

  const name = summary?.account.name ?? "Account";

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full space-y-5">
        <PageHeader title={name} description="Invoice history, contracts and billing periods">
          {can("invoices.create") && can("contracts.view") && <ContractInvoiceLauncher accountId={clientId} />}
          <Link to="/invoices" className={btnGhost}>All accounts</Link>
          {can("invoices.configure") && <button className={btnGhost} onClick={() => setSettingsOpen(true)}>Invoice settings</button>}
        </PageHeader>

        {summary && summary.contracts.some((c) => c.periods_missing > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3">
            <p className="text-sm text-gray-800">
              <strong>{summary.contracts.reduce((n, c) => n + c.periods_missing, 0)} billing period(s)</strong> have ended without an invoice — for example contracts that were added after their effective date. Past invoices are optional. Open a contract to ignore periods you do not need.
            </p>
            <div className="flex flex-wrap gap-2"><button className={btnPrimary} onClick={() => openGenerate(summary.contracts.find((c) => c.periods_missing > 0)!)}>Generate invoice from contract</button>{can('contracts.update') && <button className={btnGhost} onClick={async () => { try { for (const contract of summary.contracts.filter((item) => item.periods_missing > 0)) { const response = await get<{ data: { periods: Period[] } }>(`/contracts/${contract.id}/billing-periods`); const starts = response.data.periods.filter((period) => period.status === 'missing').map((period) => period.period_start); if (starts.length) await put(`/contracts/${contract.id}/ignored-periods`, { period_starts: starts, ignored: true }); } setNotice('Past missing billing periods ignored.'); await loadSummary(); } catch (failure) { setNotice(parseApiError(failure).message); } }}>Ignore past missing periods</button>}</div>
          </div>
        )}

        {summary && summary.suspected_mistakes.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-900"><strong>{summary.suspected_mistakes.length} invoice{summary.suspected_mistakes.length === 1 ? "" : "s"}</strong> {summary.suspected_mistakes.length === 1 ? "looks like it was" : "look like they were"} generated by mistake ({Array.from(new Set(summary.suspected_mistakes.map((m) => MISTAKE_REASONS[m.reason]))).join("; ")}).</p>{can('invoices.update') && <div className="mt-3 flex flex-wrap gap-2">{summary.suspected_mistakes.map((invoice) => <button type="button" key={invoice.id} className={btnGhost} onClick={() => ignoreInvoice(invoice.id, true)}>Ignore {invoice.invoice_number}</button>)}</div>}
            {admin ? <button className={btnDanger} onClick={() => { setCleanupError(""); setCleanupOpen(true); }}>Review & delete</button> : <span className="text-xs text-amber-800">An administrator can delete them.</span>}
          </div>
        )}

        {notice && <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-2 text-xs text-green-800">{notice}</div>}

        <GridProvider id="account-invoices">
        <div className="space-y-4">
        <GridToolbar
          filters={<>
            <label className="text-xs text-gray-600">Status<AppSelect value={status} onChange={(e) => setStatus(e.target.value)} className={`${inputCls} mt-1`}>{STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</AppSelect></label>
            <label className="text-xs text-gray-600">Contract
              <AppSelect value={contractId} onChange={(e) => setContractId(e.target.value)} className={`${inputCls} mt-1`}>
                <option value="">All contracts</option>
                {summary?.contracts.map((c) => <option key={c.id} value={c.id}>{c.service_type.replace(/_/g, " ")} ({formatDate(c.start_date)})</option>)}
              </AppSelect>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <DatePicker value={periodFrom} onChange={setPeriodFrom} placeholder="Period from" max={periodTo || undefined} clearable aria-label="Period from" />
              <DatePicker value={periodTo} onChange={setPeriodTo} placeholder="Period to" min={periodFrom || undefined} clearable aria-label="Period to" />
            </div>
          </>}
          activeFilters={[status, contractId, periodFrom, periodTo].filter(Boolean).length}
          onClearFilters={() => { setStatus(""); setContractId(""); setPeriodFrom(""); setPeriodTo(""); }}
          hasStats={!!summary && summary.totals.length > 0}
          onAdd={() => navigate(`/invoices/client/${clientId}/new`)}
          addLabel="Create Invoice"
        />
        {summary && summary.totals.length > 0 && (
          <StatsStrip items={[
            { label: "Billed", value: showTotal(summary.display_totals, "billed", summary.totals[0]), hint: `${summary.totals.reduce((n, t) => n + t.invoice_count, 0)} invoices` },
            { label: "Income received", value: showTotal(summary.display_totals, "paid", summary.totals[0]), tone: "good" },
            { label: "Outstanding", value: showTotal(summary.display_totals, "outstanding", summary.totals[0]), tone: "warn" },
            { label: "Overdue", value: showTotal(summary.display_totals, "overdue", summary.totals[0]), tone: "bad" },
          ]} />
        )}

        {summary && summary.contracts.length > 0 && (
          <section className="rounded-xl border border-gray-200 bg-white">
            <div className="px-4 py-3 border-b border-gray-100"><h2 className="text-sm font-semibold text-gray-900">Contracts</h2></div>
            <div className="divide-y divide-gray-100">
              {summary.contracts.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-900 capitalize">{c.service_type.replace(/_/g, " ")} <span className="ml-1 font-normal text-gray-500">· {c.billing_mode === "percentage" ? "Percentage instalments" : c.billing_cycle.replace("_", " ")} · {c.status}</span></p>
                    <p className="text-xs text-gray-500">Effective {formatDate(c.start_date)} → {formatDate(c.end_date)} · {c.billing_mode === "variable" ? "Based on delivered quantities" : `${c.value ? showMoney(c, "value") : "no value"} ${c.billing_mode === "percentage" ? "total contract value" : "per period"}`} · {c.billing_mode === "percentage" ? "Invoice when due" : `${c.invoices_count}/${c.periods_total} periods invoiced`}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link className={btnGhost} to={`/contracts/${c.id}`}>View contract</Link>
                    <button className={btnGhost} onClick={() => setContractId(contractId === c.id ? "" : c.id)}>{contractId === c.id ? "Show all invoices" : "View invoices"}</button>
                    {can('invoices.create') && (
                      <button className={c.periods_missing ? btnPrimary : btnGhost} onClick={() => openGenerate(c)}>
                        Generate invoice from contract
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 animate-pulse space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 bg-gray-100 rounded" />)}</div>
          ) : invoices.length ? (
            <TableProvider columns={columns} data={invoices} onDeleteSelected={admin ? (rows) => bulkDelete((rows as Invoice[]).map((i) => i.id)) : undefined}>
              <DataTableHeader>{({ headerGroup }) => <DataTableHeaderGroup headerGroup={headerGroup}>{({ header }) => <DataTableHead header={header} />}</DataTableHeaderGroup>}</DataTableHeader>
              <DataTableBody>{({ row }) => (
                <DataTableRow row={row} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/invoices/${(row.original as Invoice).id}`)}>
                  {({ cell }) => <DataTableCell cell={cell} />}
                </DataTableRow>
              )}</DataTableBody>
            </TableProvider>
          ) : (
            <p className="p-10 text-center text-sm text-gray-500">No invoices for this account yet.</p>
          )}
          <PaginationBar page={page} totalPages={pager.total_pages} total={pager.total} perPage={perPage} onPage={setPage} onPerPage={(n) => { setPerPage(n); setPage(1); }} noun="invoices" />
        </div>
        </div>
        </GridProvider>
      </div>

      <ClientInvoiceSettings accountId={clientId!} open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={() => { setNotice("Invoice settings saved. Existing amounts and payments are unchanged."); loadSummary(); loadInvoices(); }} />

      <Modal
        wide
        open={!!genContract}
        onClose={() => setGenContract(null)}
        title={genContract?.billing_mode === "percentage" ? "Percentage invoice" : "Billing periods"}
        footer={<><button className={btnGhost} onClick={() => setGenContract(null)}>Close</button><button className={btnPrimary} disabled={genBusy || !validInitialPayment(initialPayment) || (genContract?.billing_mode !== "percentage" && initialPayment !== null && selected.size !== 1) || (genContract?.billing_mode === "percentage" ? !validPercentage(percentageDraft) : selected.size === 0) || (genContract?.billing_mode === "variable" && !genQuantities.some((quantity) => Number(quantity) > 0))} onClick={generate}>{genBusy ? "Generating…" : (genContract?.billing_mode === "percentage" ? "Generate percentage invoice" : `Generate ${selected.size} invoice${selected.size === 1 ? "" : "s"}`)}</button></>}
      >
        {genContract && (
          <>
            {genContract.billing_mode === "percentage" ? <PercentageInvoiceFields value={percentageDraft} onChange={setPercentageDraft} contractValue={genContract.value} currency={genContract.currency} billed={genContract.contract_billed_total} /> : <><p className="text-xs text-gray-600">Billing runs from the contract's effective date, <strong>{formatDate(genContract.start_date)}</strong>, not from when it was entered. A period can only be invoiced after its last day, and one that already has an invoice can't be generated again.</p>
            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200">
              {periods.map((p) => (
                <label key={p.period_start} className="flex items-center gap-3 px-3 py-2 text-xs">
                  <input type="checkbox" className="accent-primary" disabled={p.status !== "missing"} checked={p.status === "missing" && selected.has(p.period_start)}
                    onChange={(e) => { if (genContract.billing_mode === "variable") setGenQuantities(emptyDeliveredQuantities(genLines)); setSelected((s) => { const n = new Set(s); if (e.target.checked) { if (genContract.billing_mode === "variable") n.clear(); n.add(p.period_start); } else n.delete(p.period_start); return n; }); }} />
                  <span className="flex-1">{formatDate(p.period_start)} – {formatDate(p.period_end)}</span>
                  {p.invoice ? <Link to={`/invoices/${p.invoice.id}`} className="text-primary underline">{p.invoice.invoice_number}</Link> : <span className={p.status === "ignored" ? "text-gray-500" : "text-amber-700"}>{p.status === "ignored" ? "Ignored" : "Ready to invoice"}</span>}
                </label>
              ))}
              {!periods.length && <p className="p-4 text-center text-gray-500">No billing period has ended yet — invoices are issued once a period's last day has passed.</p>}
            </div>
            </>}
            <InvoicePresentationFields value={genDefaults} onChange={setGenDefaults} />
            {genContract.billing_mode === "variable" && <div className="space-y-3"><p className="text-xs text-gray-500">Choose one period and enter exactly what was delivered. Its total can differ each month.</p><input aria-label="Invoice description" className={inputCls} value={genDescription} onChange={(e) => setGenDescription(e.target.value)} /><DeliveredItemsEditor rates={genLines} quantities={genQuantities} onChange={setGenQuantities} currency={genContract.currency} /></div>}
            {can('payments.create') && <InitialPaymentFields value={initialPayment} onChange={setInitialPayment} currency={genContract.currency} disabled={genBusy} />}
            {genContract.billing_mode !== "percentage" && initialPayment && selected.size !== 1 && <p className="text-xs text-amber-700">Choose exactly one billing period to record its payment.</p>}
            {genError && <p className="text-xs text-red-600">{genError}</p>}
          </>
        )}
      </Modal>

      <Modal
        open={cleanupOpen}
        onClose={() => setCleanupOpen(false)}
        title="Delete invoices generated by mistake"
        footer={<><button className={btnGhost} onClick={() => setCleanupOpen(false)}>Cancel</button><button className={btnDanger} disabled={cleanupBusy} onClick={() => bulkDelete((summary?.suspected_mistakes ?? []).map((m) => m.id))}>{cleanupBusy ? "Deleting…" : `Delete ${summary?.suspected_mistakes.length ?? 0}`}</button></>}
      >
        <p className="text-xs text-gray-600">None of these have payments. They are removed permanently (a copy stays in the audit log), and any that belong to an ended period can be generated again.</p>
        <ul className="max-h-64 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 text-xs">
          {summary?.suspected_mistakes.map((m) => (
            <li key={m.id} className="flex items-center justify-between gap-3 px-3 py-2"><span className="font-medium text-gray-900">{m.invoice_number}{m.period_start ? ` · ${formatDate(m.period_start)}` : ""}</span><span className="text-amber-700">{MISTAKE_REASONS[m.reason]}</span></li>
          ))}
        </ul>
        {cleanupError && <p className="text-xs text-red-600">{cleanupError}</p>}
      </Modal>
    </div>
  );
}
